import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class TabLockService {
  // Names this tab currently holds → their release fns. Replaces the old single
  // (ownedLockName + releaseImpersonationLock) pair; the map key is the ownership marker.
  private releasers = new Map<string, () => void>();

  /** True if the browser supports the Web Locks API (HTTPS / localhost only). */
  supportsLocks(): boolean {
    return typeof navigator !== 'undefined'
      && !!navigator.locks
      && typeof navigator.locks.request === 'function';
  }

  /**
   * Take ownership of a name for as long as this tab stays open, via the Web Locks API. A name can
   * be held by only one open tab at a time; the hold releases automatically when the tab closes.
   * Returns true if we got it, false if another open tab has it (or the browser lacks Web Locks).
   */
  claim(name: string): Promise<boolean> {
    if (!this.supportsLocks()) {
      return Promise.resolve(false);
    }
    // We already hold this one; asking again would hang forever and leak.
    if (this.releasers.has(name)) {
      return Promise.resolve(true);
    }

    return new Promise<boolean>(resolve => {
      // ifAvailable: don't wait in line — if another open tab has it, fail immediately.
      navigator.locks.request(name, { ifAvailable: true }, lock => {
        if (!lock) {
          resolve(false);
          return Promise.resolve();
        }
        resolve(true);
        // Keep holding it: this promise stays pending until we release it or the tab closes.
        return new Promise<void>(release => {
          this.releasers.set(name, () => {
            this.releasers.delete(name);
            release();
          });
        });
      }).catch(err => {
        console.error('Failed to claim tab lock', err);
        resolve(false);
      });
    });
  }

  /** Give up a previously claimed name. No-op if not held. */
  release(name: string): void {
    this.releasers.get(name)?.();
  }
}
